import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate, useSearchParams } from "react-router-dom";

import "./themeupdate.css";

import { fetchThemeData } from "../../themeSlice";
import {
  selectAdminToken,
  selectOrganizationId,
  selectSessionId,
} from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import { ensureThemeJsonArray } from "../../../functions/themeAssets";
import {
  buildWordsFromPairs,
  generateLayout,
  getWordStartPositions,
  serializeCrosswordLayout,
} from "./crosswordLayout";
import { clampQuizFields, fillLegacyQuizOptions, validateQuizList } from "./quizHelpers";
import QuizTab from "./QuizTab";
import {
  clampQuizFields as clampAudioQuizFields,
  validateQuizList as validateAudioQuizList,
} from "./audioQuizHelpers";
import AudioQuizTab from "./AudioQuizTab";
import MatchPairTab, {
  normalizeMatchPairsFromTheme,
  validateMatchPairs,
} from "./MatchPairTab";
import { renameFileForUpload } from "./safeUploadFileName";
import {
  STAGE_HEADING_MAX_LEN,
  STAGE_SUBHEADING_MAX_LEN,
  validateStageHeadingSubheading,
} from "./StageRulesEditor";
import ThemeUpdateFeatureTour, {
  markThemeUpdateTourSeen,
  shouldAutoStartThemeUpdateTour,
} from "./ThemeUpdateFeatureTour";

const GAME_TABS = [
  { id: "quiz", label: "Quiz" },
  { id: "downAndAcross", label: "Down and Across" },
  { id: "audio", label: "Audio" },
  { id: "matchPair", label: "Match Pair" },
];

const DEFAULT_GAME_TAB = GAME_TABS[0].id;
const GAME_TAB_PARAM = "tab";

function getGameTabFromSearch(params) {
  const tab = params.get(GAME_TAB_PARAM);
  return GAME_TABS.some((t) => t.id === tab) ? tab : DEFAULT_GAME_TAB;
}

const MAX_PAIRS = 8;
const MIN_PAIRS = 2;
const QUESTION_MAX_LEN = 80;
const ANSWER_MAX_LEN = 15;

const DEFAULT_DOWN_AND_ACROSS_SUBTITLE =
  "Complete the crossword by solving the clues given below.";

const DEFAULT_QUIZ_HEADING = "Quiz";
const DEFAULT_QUIZ_SUBTITLE = "Drag each statement into the category it belongs to.";
const DEFAULT_AUDIO_HEADING = "Audio Quiz";
const DEFAULT_AUDIO_SUBTITLE = "Listen to each clip and choose the correct answer.";
const DEFAULT_MATCH_PAIR_HEADING = "Match-up game";
const DEFAULT_MATCH_PAIR_SUBTITLE = "Match names to images: left pick, right match.";

const clampStageHeading = (raw) => String(raw ?? "").slice(0, STAGE_HEADING_MAX_LEN);
const clampStageSubheading = (raw) => String(raw ?? "").slice(0, STAGE_SUBHEADING_MAX_LEN);

let pairIdCounter = 0;
function newPairId(prefix = "pair") {
  pairIdCounter += 1;
  return `${prefix}-${pairIdCounter}`;
}

function emptyPair() {
  return { id: newPairId("da"), question: "", answer: "" };
}

function normalizePairsFromTheme(raw) {
  const list = ensureThemeJsonArray(raw);
  if (!list.length) {
    return [emptyPair(), emptyPair()];
  }
  return list.map((item) => ({
    id: newPairId("da"),
    question: String(item?.question ?? "").slice(0, QUESTION_MAX_LEN),
    answer: String(item?.answer ?? "").slice(0, ANSWER_MAX_LEN),
  }));
}

function normalizeQuizFromTheme(raw, categories) {
  return ensureThemeJsonArray(raw).map((item) => ({
    id: newPairId("quiz"),
    ...clampQuizFields(fillLegacyQuizOptions(item, categories)),
  }));
}

function normalizeAudioQuizFromTheme(raw) {
  return ensureThemeJsonArray(raw).map((item) => ({
    id: newPairId("audio"),
    ...clampAudioQuizFields(item),
  }));
}

function CrosswordPreview({ layout }) {
  if (!layout?.table?.length) {
    return (
      <p className="themeupdate-muted crossword-preview-empty">
        Add at least two complete question/answer pairs to preview the grid.
      </p>
    );
  }

  const { table, result: words, rows, cols } = layout;
  const wordStartPositions = getWordStartPositions(words);

  return (
    <div className="crossword-preview-wrap">
      <div
        id="crossword"
        className="crossword-grid"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
      >
        {Array.from({ length: rows }, (_, i) =>
          Array.from({ length: cols }, (_, j) => {
            const letter = table[i][j];
            const isBlock = letter === "-";
            const positionKey = `${i + 1},${j + 1}`;
            const cellNumber = wordStartPositions[positionKey];

            return (
              <div
                key={`${i}-${j}`}
                className={`crossword-cell${isBlock ? " crossword-cell--block" : ""}`}
              >
                {!isBlock ? (
                  <>
                    {cellNumber ? (
                      <span className="crossword-cell-number">{cellNumber}</span>
                    ) : null}
                    <span className="crossword-cell-letter">{letter.toUpperCase()}</span>
                  </>
                ) : null}
              </div>
            );
          })
        )}
      </div>
      <div className="crossword-clues">
        <div className="crossword-clues-section">
          <h4>Across</h4>
          {words
            .filter((w) => w.orientation === "across")
            .map((w) => (
              <p key={`across-${w.starty}-${w.startx}-${w.answer}`} className="crossword-clue">
                {wordStartPositions[`${w.starty},${w.startx}`]}. {w.clue}
              </p>
            ))}
        </div>
        <div className="crossword-clues-section">
          <h4>Down</h4>
          {words
            .filter((w) => w.orientation === "down")
            .map((w) => (
              <p key={`down-${w.starty}-${w.startx}-${w.answer}`} className="crossword-clue">
                {wordStartPositions[`${w.starty},${w.startx}`]}. {w.clue}
              </p>
            ))}
        </div>
      </div>
    </div>
  );
}

function DownAndAcrossTab({
  pairs,
  setPairs,
  layout,
  layoutError,
  isSaving,
  onSave,
  errorMessage,
  successMessage,
  setErrorMessage,
  setSuccessMessage,
}) {
  const addPair = () => {
    if (pairs.length >= MAX_PAIRS) {
      setErrorMessage(`You can add up to ${MAX_PAIRS} pairs.`);
      return;
    }
    setErrorMessage("");
    setPairs((prev) => [...prev, emptyPair()]);
  };

  const removePair = (id) => {
    if (pairs.length <= MIN_PAIRS) {
      setErrorMessage(`You need at least ${MIN_PAIRS} pairs.`);
      return;
    }
    setPairs((prev) => prev.filter((p) => p.id !== id));
    setErrorMessage("");
  };

  const updatePair = (id, field, value) => {
    const max = field === "question" ? QUESTION_MAX_LEN : ANSWER_MAX_LEN;
    setPairs((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value.slice(0, max) } : p))
    );
    setErrorMessage("");
    setSuccessMessage("");
  };

  const clearAll = () => {
    setPairs([emptyPair(), emptyPair()]);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const leftPairs = pairs.filter((_, i) => i % 2 === 0);
  const rightPairs = pairs.filter((_, i) => i % 2 === 1);

  const renderPairCard = (pair) => (
    <div key={pair.id} className="qa-pair-card">
      <label htmlFor={`q-${pair.id}`}>Question</label>
      <input
        id={`q-${pair.id}`}
        type="text"
        value={pair.question}
        maxLength={QUESTION_MAX_LEN}
        onChange={(e) => updatePair(pair.id, "question", e.target.value)}
        placeholder="Question"
      />
      <button
        type="button"
        className="qa-pair-delete"
        onClick={() => removePair(pair.id)}
        aria-label="Delete pair"
        title="Delete pair"
      >
        <i className="fa-regular fa-trash-can" />
      </button>
      <label htmlFor={`a-${pair.id}`}>Answer</label>
      <input
        id={`a-${pair.id}`}
        type="text"
        value={pair.answer}
        maxLength={ANSWER_MAX_LEN}
        onChange={(e) => updatePair(pair.id, "answer", e.target.value)}
        placeholder="Answer"
      />
    </div>
  );

  return (
    <div className="themeupdate-panel-wide down-across-panel" data-tour="tour-gc-down-across">
      <p className="qa-char-limit-hint">
        * Add {MIN_PAIRS}–{MAX_PAIRS} complete pairs. Maximum character limit: Question —{" "}
        {QUESTION_MAX_LEN} characters, Answer — {ANSWER_MAX_LEN} characters.
      </p>

      {(errorMessage || successMessage) && (
        <div className="qa-alerts">
          {errorMessage ? <p className="cr-alert cr-alert-error">{errorMessage}</p> : null}
          {successMessage ? <p className="cr-alert cr-alert-success">{successMessage}</p> : null}
        </div>
      )}

      <div className="qa-pairs-columns">
        <div className="qa-pairs-col">{leftPairs.map(renderPairCard)}</div>
        <div className="qa-pairs-col">{rightPairs.map(renderPairCard)}</div>
      </div>

      <div className="qa-pairs-actions">
        <button
          type="button"
          className="qa-btn qa-btn-outline"
          onClick={addPair}
          disabled={pairs.length >= MAX_PAIRS}
        >
          Add Pair
        </button>
        <button
          type="button"
          className="qa-btn qa-btn-primary"
          onClick={onSave}
          disabled={isSaving}
        >
          {isSaving ? "Saving…" : "Save and Continue"}
        </button>
        <button type="button" className="qa-btn qa-btn-primary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      <div className="themeupdate-card crossword-preview-card">
        <div className="themeupdate-card-header">Grid preview</div>
        <div className="themeupdate-card-body">
          {layoutError ? <p className="cr-alert cr-alert-error">{layoutError}</p> : null}
          <CrosswordPreview layout={layout} />
        </div>
      </div>
    </div>
  );
}

function StageHeadingSubheadingEditor({
  stageLabel,
  heading,
  setHeading,
  subheading,
  setSubheading,
  onSave,
  isSaving = false,
  saveLabel = "Save",
}) {
  return (
    <div className="themeupdate-panel-wide">
      <div className="themeupdate-card">
        <div className="themeupdate-card-body">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            <div style={{ flex: "1 1 320px", minWidth: 280 }}>
              <label className="themeupdate-label">{stageLabel} heading</label>
              <input
                type="text"
                className="themeupdate-input"
                value={heading}
                maxLength={STAGE_HEADING_MAX_LEN}
                onChange={(e) => setHeading(clampStageHeading(e.target.value))}
              />
            </div>

            <div style={{ flex: "1 1 320px", minWidth: 280 }}>
              <label className="themeupdate-label">{stageLabel} sub heading</label>
              <textarea
                className="themeupdate-input"
                rows={2}
                value={subheading}
                maxLength={STAGE_SUBHEADING_MAX_LEN}
                onChange={(e) => setSubheading(clampStageSubheading(e.target.value))}
              />
            </div>
          </div>

          <p className="themeupdate-muted" style={{ marginTop: 8 }}>
            * Heading — max {STAGE_HEADING_MAX_LEN} characters · Sub heading — max{" "}
            {STAGE_SUBHEADING_MAX_LEN} characters.
          </p>

          {onSave ? (
            <div className="stage-heading-save-wrap">
              <button
                type="button"
                className="qa-btn qa-btn-primary"
                onClick={onSave}
                disabled={isSaving}
              >
                {isSaving ? "Saving…" : saveLabel}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const activeTab = getGameTabFromSearch(searchParams);
  const [isSaving, setIsSaving] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizErrorMessage, setQuizErrorMessage] = useState("");
  const [quizSuccessMessage, setQuizSuccessMessage] = useState("");
  const [tourOpen, setTourOpen] = useState(false);

  const [audioQuestions, setAudioQuestions] = useState([]);
  const [audioErrorMessage, setAudioErrorMessage] = useState("");
  const [audioSuccessMessage, setAudioSuccessMessage] = useState("");

  const [quizHeading, setQuizHeading] = useState(DEFAULT_QUIZ_HEADING);
  const [quizSubheading, setQuizSubheading] = useState(DEFAULT_QUIZ_SUBTITLE);
  const [audioHeading, setAudioHeading] = useState(DEFAULT_AUDIO_HEADING);
  const [audioSubheading, setAudioSubheading] = useState(DEFAULT_AUDIO_SUBTITLE);

  const [matchPairs, setMatchPairs] = useState(() => normalizeMatchPairsFromTheme([]));
  const [matchPairHeading, setMatchPairHeading] = useState(DEFAULT_MATCH_PAIR_HEADING);
  const [matchPairSubheading, setMatchPairSubheading] = useState(DEFAULT_MATCH_PAIR_SUBTITLE);
  const [mpErrorMessage, setMpErrorMessage] = useState("");
  const [mpSuccessMessage, setMpSuccessMessage] = useState("");

  const [pairs, setPairs] = useState(() => [emptyPair(), emptyPair()]);
  const [daErrorMessage, setDaErrorMessage] = useState("");
  const [daSuccessMessage, setDaSuccessMessage] = useState("");
  const [downAndAcrossHeading, setDownAndAcrossHeading] = useState("");
  const [downAndAcrossSubheading, setDownAndAcrossSubheading] = useState(
    DEFAULT_DOWN_AND_ACROSS_SUBTITLE
  );

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setQuizQuestions(
      normalizeQuizFromTheme(data.quiz, ensureThemeJsonArray(data.categories))
    );
    setAudioQuestions(normalizeAudioQuizFromTheme(data.audio_quiz));

    setQuizHeading(clampStageHeading(data.quiz_title || DEFAULT_QUIZ_HEADING));
    setQuizSubheading(clampStageSubheading(data.quiz_subtitle ?? DEFAULT_QUIZ_SUBTITLE));
    setAudioHeading(clampStageHeading(data.audio_quiz_title || DEFAULT_AUDIO_HEADING));
    setAudioSubheading(
      clampStageSubheading(data.audio_quiz_subtitle ?? DEFAULT_AUDIO_SUBTITLE)
    );

    setPairs(normalizePairsFromTheme(data.questions_and_answers));
    const titleFallback = String(data.themename ?? "").trim();
    setDownAndAcrossHeading(
      clampStageHeading(data.downandacross_title || titleFallback || "Down and Across")
    );
    setDownAndAcrossSubheading(
      clampStageSubheading(data.downandacross_subtitle ?? DEFAULT_DOWN_AND_ACROSS_SUBTITLE)
    );

    setMatchPairs(normalizeMatchPairsFromTheme(data.match_pairs));
    setMatchPairHeading(clampStageHeading(data.match_pair_title || DEFAULT_MATCH_PAIR_HEADING));
    setMatchPairSubheading(
      clampStageSubheading(data.match_pair_subtitle ?? DEFAULT_MATCH_PAIR_SUBTITLE)
    );
  }, [data]);

  useEffect(() => {
    if (!data) return;
    if (!shouldAutoStartThemeUpdateTour()) return;
    const t = window.setTimeout(() => setTourOpen(true), 400);
    return () => window.clearTimeout(t);
  }, [data]);

  const closeTour = useCallback(() => {
    markThemeUpdateTourSeen();
    setTourOpen(false);
  }, []);

  const layout = useMemo(() => {
    const words = buildWordsFromPairs(pairs);
    if (words.length < MIN_PAIRS) return null;
    try {
      const wordsCopy = words.map((w) => ({ ...w }));
      return generateLayout(wordsCopy);
    } catch {
      return null;
    }
  }, [pairs]);

  const layoutError = useMemo(() => {
    const words = buildWordsFromPairs(pairs);
    if (words.length < MIN_PAIRS) return null;
    if (!layout?.table?.length) {
      return "Could not generate a crossword from these answers. Try shorter answers or different words.";
    }
    const placed = Object.values(layout.result || {}).filter(
      (w) => w.orientation && w.orientation !== "none"
    );
    if (placed.length < words.length) {
      return `Only ${placed.length} of ${words.length} answers were placed on the grid. Some words may be too long or incompatible.`;
    }
    return null;
  }, [pairs, layout]);

  const handleSaveDownAndAcross = async () => {

    const filled = pairs
      .map((p) => ({
        question: p.question.trim(),
        answer: p.answer.trim(),
      }))
      .filter((p) => p.question || p.answer);

    const complete = filled.filter((p) => p.question && p.answer);

    if (complete.length < MIN_PAIRS) {
      setDaErrorMessage(`Please add at least ${MIN_PAIRS} complete question/answer pairs before saving.`);
      setDaSuccessMessage("");
      return;
    }

    if (complete.length > MAX_PAIRS) {
      setDaErrorMessage(`You can save up to ${MAX_PAIRS} question/answer pairs.`);
      setDaSuccessMessage("");
      return;
    }

    const hasEmpty = filled.some((p) => !p.question || !p.answer);
    if (hasEmpty) {
      setDaErrorMessage("Please fill in all fields or remove incomplete pairs.");
      setDaSuccessMessage("");
      return;
    }

    const questionTooLong = complete.find((p) => p.question.length > QUESTION_MAX_LEN);
    if (questionTooLong) {
      setDaErrorMessage(`Each question must be ${QUESTION_MAX_LEN} characters or fewer.`);
      return;
    }

    const answerTooLong = complete.find((p) => p.answer.length > ANSWER_MAX_LEN);
    if (answerTooLong) {
      setDaErrorMessage(`Each answer must be ${ANSWER_MAX_LEN} characters or fewer.`);
      return;
    }

    if (layoutError && !layout?.table?.length) {
      setDaErrorMessage(layoutError);
      return;
    }

    const crosswordLayout = serializeCrosswordLayout(layout);

    setIsSaving(true);
    setDaErrorMessage("");
    setDaSuccessMessage("");

    try {
      const payload = {
        questions_and_answers: complete,
      };
      if (crosswordLayout) {
        payload.crossword_layout = crosswordLayout;
      }

      await updateThemeData({
        payload: {
          data: payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Down and Across data saved to theme.", "success");
      setDaSuccessMessage("Saved to theme.");
    } catch (err) {
      setDaErrorMessage(err?.message || "Failed to save data.");
    } finally {
      setIsSaving(false);
    }
  };

  const persistQuizQuestions = async (nextQuestions) => {
    const validation = validateQuizList(nextQuestions.map(({ id, ...rest }) => rest));
    if (!validation.ok) {
      throw new Error(validation.message);
    }

    setIsSaving(true);
    setQuizErrorMessage("");
    setQuizSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            quiz: validation.data,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      setQuizQuestions(
        nextQuestions.map((q, i) => ({
          id: q.id || newPairId("quiz"),
          ...validation.data[i],
        }))
      );
    } catch (err) {
      setQuizErrorMessage(err?.message || "Failed to save questions to theme.");
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  /** Save just a stage's heading + sub heading (for tabs whose content saves per question). */
  const saveStageHeadings = async ({
    heading,
    subheading,
    stageLabel,
    titleKey,
    subtitleKey,
    setError,
    setSuccess,
  }) => {
    const stageMeta = validateStageHeadingSubheading(heading, subheading, stageLabel);
    if (!stageMeta.ok) {
      setError(stageMeta.message);
      setSuccess("");
      return;
    }

    setIsSaving(true);
    setError("");
    setSuccess("");

    try {
      await updateThemeData({
        payload: {
          data: {
            [titleKey]: stageMeta.heading,
            [subtitleKey]: stageMeta.subheading,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", `${stageLabel} heading saved to theme.`, "success");
      setSuccess("Heading saved to theme.");
    } catch (err) {
      setError(err?.message || `Failed to save ${stageLabel} heading.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveQuizHeadings = () =>
    saveStageHeadings({
      heading: quizHeading,
      subheading: quizSubheading,
      stageLabel: "Quiz",
      titleKey: "quiz_title",
      subtitleKey: "quiz_subtitle",
      setError: setQuizErrorMessage,
      setSuccess: setQuizSuccessMessage,
    });

  const handleSaveAudioHeadings = () =>
    saveStageHeadings({
      heading: audioHeading,
      subheading: audioSubheading,
      stageLabel: "Audio",
      titleKey: "audio_quiz_title",
      subtitleKey: "audio_quiz_subtitle",
      setError: setAudioErrorMessage,
      setSuccess: setAudioSuccessMessage,
    });

  const handleSaveDownAndAcrossHeadings = () =>
    saveStageHeadings({
      heading: downAndAcrossHeading,
      subheading: downAndAcrossSubheading,
      stageLabel: "Down and Across",
      titleKey: "downandacross_title",
      subtitleKey: "downandacross_subtitle",
      setError: setDaErrorMessage,
      setSuccess: setDaSuccessMessage,
    });

  const handleSaveMatchPairHeadings = () =>
    saveStageHeadings({
      heading: matchPairHeading,
      subheading: matchPairSubheading,
      stageLabel: "Match Pair",
      titleKey: "match_pair_title",
      subtitleKey: "match_pair_subtitle",
      setError: setMpErrorMessage,
      setSuccess: setMpSuccessMessage,
    });

  const persistAudioQuestions = async (nextQuestions) => {
    const validation = validateAudioQuizList(nextQuestions.map(({ id, ...rest }) => rest));
    if (!validation.ok) {
      throw new Error(validation.message);
    }

    setIsSaving(true);
    setAudioErrorMessage("");
    setAudioSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            audio_quiz: validation.data,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      setAudioQuestions(
        nextQuestions.map((q, i) => ({
          id: q.id || newPairId("audio"),
          ...validation.data[i],
        }))
      );
    } catch (err) {
      setAudioErrorMessage(err?.message || "Failed to save audio questions.");
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMatchPairs = async () => {
    const pairsError = validateMatchPairs(matchPairs);
    if (pairsError) {
      setMpErrorMessage(pairsError);
      setMpSuccessMessage("");
      return;
    }

    if (!adminToken || !currentTheme) {
      setMpErrorMessage("Admin session or theme is missing.");
      return;
    }

    setIsSaving(true);
    setMpErrorMessage("");
    setMpSuccessMessage("");

    try {
      // Upload any newly picked images first (upload-only — nothing is written to the theme).
      let uploaded = {};
      const pending = matchPairs
        .map((p, i) => ({ field: `match_pair_img_${i}`, file: p.file }))
        .filter((p) => p.file);
      if (pending.length) {
        const formData = new FormData();
        formData.append("themeName", currentTheme);
        formData.append("organizationId", organizationId || "admin");
        formData.append("sessionId", sessionId || "admin");
        formData.append("uploadOnly", "true");
        pending.forEach(({ field, file }) =>
          formData.append(field, renameFileForUpload(file, "match"))
        );
        const result = await uploadThemeMedia({ formData, token: adminToken });
        uploaded = result?.uploaded_files || {};
      }

      const payloadPairs = matchPairs.map((p, i) => {
        const image = p.file ? uploaded[`match_pair_img_${i}`] : p.image;
        if (!image) throw new Error(`Image upload failed for pair ${i + 1}. Please try again.`);
        return { image, answer: p.answer.trim() };
      });

      await updateThemeData({
        payload: {
          data: {
            match_pairs: payloadPairs,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Match pairs saved to theme.", "success");
      setMpSuccessMessage("Saved to theme.");
    } catch (err) {
      setMpErrorMessage(err?.message || "Failed to save match pairs.");
    } finally {
      setIsSaving(false);
    }
  };

  const changeTab = useCallback(
    (tabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tabId === DEFAULT_GAME_TAB) {
            next.delete(GAME_TAB_PARAM);
          } else {
            next.set(GAME_TAB_PARAM, tabId);
          }
          return next;
        },
        { replace: true }
      );
      setQuizErrorMessage("");
      setQuizSuccessMessage("");
      setDaErrorMessage("");
      setDaSuccessMessage("");
      setAudioErrorMessage("");
      setAudioSuccessMessage("");
      setMpErrorMessage("");
      setMpSuccessMessage("");
    },
    [setSearchParams]
  );

  const handleTourStepChange = useCallback(
    (step) => {
      if (!step?.tab) return;
      changeTab(step.tab);
    },
    [changeTab]
  );

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <header className="themeupdate-header themeupdate-panel-wide" data-tour="tour-gc-header">
        <h1 className="themeupdate-title">Game Content</h1>
        <p className="themeupdate-muted">
          Add questions, the Down and Across crossword, audio questions, and image match pairs for this
          theme. Content is stored on the theme, not in a separate questions table.
        </p>
      </header>

      <nav className="themeupdate-tabs" aria-label="Game tabs" data-tour="tour-gc-tabs">
        {GAME_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`themeupdate-tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => changeTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === "quiz" && (
        <>
          <StageHeadingSubheadingEditor
            stageLabel="Quiz"
            heading={quizHeading}
            setHeading={setQuizHeading}
            subheading={quizSubheading}
            setSubheading={setQuizSubheading}
            onSave={handleSaveQuizHeadings}
            isSaving={isSaving}
            saveLabel="Save heading"
          />
          <QuizTab
            questions={quizQuestions}
            setQuestions={setQuizQuestions}
            isSaving={isSaving}
            onPersist={persistQuizQuestions}
            errorMessage={quizErrorMessage}
            successMessage={quizSuccessMessage}
            setErrorMessage={setQuizErrorMessage}
            setSuccessMessage={setQuizSuccessMessage}
            newQuestionId={() => newPairId("quiz")}
            adminToken={adminToken}
            currentTheme={currentTheme}
            organizationId={organizationId}
            sessionId={sessionId}
          />
        </>
      )}

      {activeTab === "downAndAcross" && (
        <>
          <StageHeadingSubheadingEditor
            stageLabel="Down and Across"
            heading={downAndAcrossHeading}
            setHeading={setDownAndAcrossHeading}
            subheading={downAndAcrossSubheading}
            setSubheading={setDownAndAcrossSubheading}
            onSave={handleSaveDownAndAcrossHeadings}
            isSaving={isSaving}
            saveLabel="Save heading"
          />
          <DownAndAcrossTab
            pairs={pairs}
            setPairs={setPairs}
            layout={layout}
            layoutError={layoutError}
            isSaving={isSaving}
            onSave={handleSaveDownAndAcross}
            errorMessage={daErrorMessage}
            successMessage={daSuccessMessage}
            setErrorMessage={setDaErrorMessage}
            setSuccessMessage={setDaSuccessMessage}
          />
        </>
      )}

      {activeTab === "audio" && (
        <>
          <StageHeadingSubheadingEditor
            stageLabel="Audio"
            heading={audioHeading}
            setHeading={setAudioHeading}
            subheading={audioSubheading}
            setSubheading={setAudioSubheading}
            onSave={handleSaveAudioHeadings}
            isSaving={isSaving}
            saveLabel="Save heading"
          />
          <AudioQuizTab
            questions={audioQuestions}
            setQuestions={setAudioQuestions}
            isSaving={isSaving}
            onPersist={persistAudioQuestions}
            errorMessage={audioErrorMessage}
            successMessage={audioSuccessMessage}
            setErrorMessage={setAudioErrorMessage}
            setSuccessMessage={setAudioSuccessMessage}
            newQuestionId={() => newPairId("audio")}
            adminToken={adminToken}
            currentTheme={currentTheme}
            organizationId={organizationId}
            sessionId={sessionId}
          />
        </>
      )}

      {activeTab === "matchPair" && (
        <>
          <StageHeadingSubheadingEditor
            stageLabel="Match Pair"
            heading={matchPairHeading}
            setHeading={setMatchPairHeading}
            subheading={matchPairSubheading}
            setSubheading={setMatchPairSubheading}
            onSave={handleSaveMatchPairHeadings}
            isSaving={isSaving}
            saveLabel="Save heading"
          />
          <MatchPairTab
            pairs={matchPairs}
            setPairs={setMatchPairs}
            isSaving={isSaving}
            onSave={handleSaveMatchPairs}
            errorMessage={mpErrorMessage}
            successMessage={mpSuccessMessage}
            setErrorMessage={setMpErrorMessage}
            setSuccessMessage={setMpSuccessMessage}
          />
        </>
      )}

      <ThemeUpdateFeatureTour
        open={tourOpen}
        onClose={closeTour}
        onStepChange={handleTourStepChange}
      />
    </div>
  );
};

export default ThemeUpdate;
